import { useQuery, keepPreviousData, type QueryKey } from '@tanstack/react-query';

export type QueryPreset = 'default' | 'longCache' | 'heavyData' | 'realtime' | 'static';

interface QueryPresetOptions {
  staleTime: number;
  gcTime: number;
  refetchOnMount?: boolean;
  refetchOnWindowFocus?: boolean;
  refetchOnReconnect?: boolean;
}

const PRESETS: Record<QueryPreset, QueryPresetOptions> = {
  /**
   * Стандартный пресет
   * - Данные свежие 5 минут
   * - Кеш хранится 10 минут
   * - Обновление при монтировании и фокусе окна
   */
  default: {
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
  },

  /**
   * Пресет для долгого кеширования
   * - Данные свежие 10 минут
   * - Кеш хранится 30 минут
   * - Без автоматического обновления
   * - Идеально для справочников и редко меняющихся данных
   */
  longCache: {
    staleTime: 10 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  },

  /**
   * Пресет для данных реального времени
   * - Данные свежие 30 секунд
   * - Кеш хранится 5 минут
   * - Обновление при любом событии
   * - Для мониторинга и дашбордов
   */
  realtime: {
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
  },

  /**
   * Пресет для тяжёлых данных (Stage 5 и т.п.)
   * - Данные свежие 10 минут (тяжёлые запросы не должны рефетчиться часто)
   * - Кеш хранится 30 минут
   * - Не рефетчится при монтировании если данные свежие
   * - Не обновляется при фокусе окна (экономия трафика)
   */
  heavyData: {
    staleTime: 10 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  },

  /**
   * Пресет для статических данных
   * - Данные никогда не устаревают
   * - Кеш хранится 1 час
   * - Загружаются один раз
   * - Для констант и неизменяемых справочников
   */
  static: {
    staleTime: Infinity,
    gcTime: 60 * 60 * 1000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  },
};

/**
 * Получить настройки пресета по имени
 */
export function getPresetOptions(preset: QueryPreset): QueryPresetOptions {
  return PRESETS[preset];
}

/**
 * Фабрика для создания типизированных хуков запросов
 * 
 * @param baseKey - Базовый ключ запроса из queryKeyFactory
 * @param fetcher - Функция загрузки данных
 * @param preset - Пресет настроек кеширования
 * @returns Хук для использования в компонентах
 * 
 * @example
 * // Создание хука
 * export const useManagerSalesPlans = createDataQuery(
 *   queryKeyFactory.managerSalesPlans.all,
 *   (bgId, managerId) => Stage5Service.load(bgId, managerId),
 *   'longCache'
 * );
 * 
 * // Использование
 * const { data, isLoading } = useManagerSalesPlans({ bgId, managerId }, true);
 */
export function createDataQuery<
  TData,
  TParams extends Record<string, unknown> = Record<string, never>
>(
  baseKey: QueryKey,
  fetcher: (params: TParams) => Promise<TData>,
  preset: QueryPreset = 'default'
) {
  return (params?: TParams, enabled: boolean = true) => {
    const queryKey = params && Object.keys(params).length > 0
      ? [...baseKey, params]
      : baseKey;

    return useQuery<TData, Error, TData>({
      queryKey,
      queryFn: () => fetcher(params ?? {} as TParams),
      enabled,
      placeholderData: keepPreviousData,
      ...PRESETS[preset],
    });
  };
}

/**
 * Типизированная версия createDataQuery с поддержкой select
 * 
 * @example
 * const useProcessedData = createSelectQuery(
 *   queryKeyFactory.someData.all,
 *   () => api.get('/data'),
 *   (data) => data.map(transformFn),
 *   'default'
 * );
 */
export function createSelectQuery<
  TData,
  TSelected,
  TParams extends Record<string, unknown> = Record<string, never>
>(
  baseKey: QueryKey,
  fetcher: (params: TParams) => Promise<TData>,
  select: (data: TData) => TSelected,
  preset: QueryPreset = 'default'
) {
  return (params?: TParams, enabled: boolean = true) => {
    const queryKey = params && Object.keys(params).length > 0
      ? [...baseKey, params]
      : baseKey;

    return useQuery<TData, Error, TSelected>({
      queryKey,
      queryFn: () => fetcher(params ?? {} as TParams),
      enabled,
      select,
      ...PRESETS[preset],
    });
  };
}

export default createDataQuery;
